<template>
  <div class="dm">
    <header class="dm__top">
      <div class="dm__row">
        <div class="dm__board">
          <desk-picker
            v-model="desk.template"
            type="statusTemplate"
            variant="soft"
            :placeholder="$t('taskDeskTemplate')"
            :set-init="true"
          />
        </div>
        <button type="button" class="dm__icon" :class="{ on: searchOpen || desk.search }" @click="toggleSearch">
          <v-icon>mdi-magnify</v-icon>
        </button>
        <button type="button" class="dm__icon" :class="{ on: desk.activeFilterCount }" @click="desk.filterOpen = true">
          <v-icon>mdi-filter-variant</v-icon>
          <span v-if="desk.activeFilterCount" class="dm__badge">{{ desk.activeFilterCount }}</span>
        </button>
      </div>
      <div v-if="searchOpen" class="dm__search">
        <v-text-field
          ref="search"
          v-model="desk.search"
          :placeholder="$t('search')"
          prepend-inner-icon="mdi-magnify"
          :loading="desk.isLoading"
          clearable
          outlined
          dense
          hide-details
        />
      </div>
      <div class="dm__views">
        <button
          v-for="v in viewList"
          :key="v.id"
          type="button"
          :class="{ on: desk.view === v.id }"
          @click="desk.view = v.id"
        >
          <v-icon small>{{ v.icon }}</v-icon>
          <span>{{ $t(v.label) }}</span>
        </button>
      </div>
      <div v-if="desk.view === 'board' && desk.columns.length" ref="tabs" class="dm__tabs">
        <button
          v-for="col in desk.columns"
          :key="col.id"
          :ref="'tab' + col.id"
          type="button"
          class="dm__tab"
          :class="{ on: activeCol && activeCol.id === col.id }"
          :style="{ '--cc': col.color || '#9aa4b2' }"
          @click="selectCol(col)"
        >
          <i class="dm__tab-dot" />
          {{ col.name }}
          <span class="dm__tab-count">{{ col.total || 0 }}</span>
        </button>
      </div>
      <v-progress-linear v-if="desk.isLoading" indeterminate height="2" color="primary" class="dm__loader" />
    </header>

    <main class="dm__body">
      <div v-if="!desk.template" class="dm__empty">
        <v-icon size="40" color="grey lighten-1">mdi-view-column-outline</v-icon>
        {{ $t('taskDeskSelectTemplate') }}
      </div>

      <!-- board: one status at a time, swipe to switch -->
      <div
        v-else-if="desk.view === 'board'"
        class="dm__board-list"
        @touchstart.passive="touchStart"
        @touchend.passive="touchEnd"
      >
        <template v-if="activeCol">
          <task-card
            v-for="t in colTasks"
            :key="t.key"
            :el="t.el"
            :color="activeCol.color"
            :done="t.done"
            mobile
            @more="openSheet(t)"
          />
          <div v-if="!colTasks.length" class="dm__empty dm__empty--inline">{{ $t('taskDeskEmpty') }}</div>
          <button v-if="desk.hasMore(activeCol)" type="button" class="dm__more" @click="desk.getTask(activeCol)">
            {{ $t('taskDeskLoadMore') }}
          </button>
          <div class="dm__quick">
            <input
              v-model="activeCol.description"
              type="text"
              :placeholder="'+ ' + $t('add-task')"
              @keydown.enter="desk.submitTaskPreview(activeCol, $event)"
            >
            <button
              v-if="activeCol.description"
              type="button"
              class="dm__quick-send"
              @click="desk.submitTaskPreview(activeCol, $event)"
            >
              <v-icon small>mdi-send</v-icon>
            </button>
          </div>
        </template>
      </div>

      <div v-else-if="desk.view === 'list'" class="dm__list">
        <section v-for="g in groups" :key="g.col.id" :style="{ '--cc': g.col.color || '#9aa4b2' }">
          <div class="dm__group">
            <i class="dm__tab-dot" />
            {{ g.col.name }}
            <span class="dm__tab-count">{{ g.col.total || 0 }}</span>
          </div>
          <div
            v-for="t in g.tasks"
            :key="t.key"
            class="dm__lrow"
            :class="{ done: t.done }"
            @click="desk.editTask('task', t.el.ref)"
          >
            <div class="dm__lrow-main">
              <div class="dm__lrow-title">{{ t.el.ref.title }}</div>
              <div class="dm__lrow-sub">
                <span v-if="t.el.ref.functor">{{ t.el.ref.functor.name }}</span>
                <span v-if="t.end" :class="{ late: isLate(t) }">
                  <v-icon x-small>mdi-calendar-blank-outline</v-icon>
                  {{ fmtDate(t.end) }}
                </span>
              </div>
            </div>
            <button type="button" class="dm__lrow-more" @click.stop="openSheet(t)">
              <v-icon small>mdi-dots-vertical</v-icon>
            </button>
          </div>
          <button v-if="desk.hasMore(g.col)" type="button" class="dm__more" @click="desk.getTask(g.col)">
            {{ $t('taskDeskLoadMore') }}
          </button>
        </section>
      </div>

      <mobile-calendar v-else-if="desk.view === 'calendar'" @more="openSheet" />

      <div v-else class="dm__gantt">
        <gantt-view compact />
      </div>
    </main>

    <button
      v-if="desk.template"
      type="button"
      class="dm__fab"
      @click="desk.openCreateTask('task', null, desk.view === 'board' ? activeCol : null, desk.template)"
    >
      <v-icon color="white">mdi-plus</v-icon>
    </button>

    <!-- task actions -->
    <v-bottom-sheet v-model="sheet" content-class="dm-sheet-wrap">
      <div v-if="sheetTask" class="dm-sheet">
        <div class="dm-sheet__grip" />
        <div class="dm-sheet__title">{{ sheetTask.el.ref.title }}</div>
        <div class="dm-sheet__status" :style="{ '--cc': sheetTask.col.color }">
          <i class="dm__tab-dot" /> {{ sheetTask.col.name }}
        </div>
        <template v-if="!moveOpen">
          <button type="button" class="dm-sheet__item" @click="act(() => desk.editTask('task', sheetTask.el.ref))">
            <v-icon>mdi-pencil-outline</v-icon>{{ $t('taskDeskOpen') }}
          </button>
          <button type="button" class="dm-sheet__item" @click="moveOpen = true">
            <v-icon>mdi-swap-horizontal</v-icon>{{ $t('taskDeskMoveTo') }}
            <v-spacer />
            <v-icon small>{{ $vuetify.rtl ? 'mdi-chevron-left' : 'mdi-chevron-right' }}</v-icon>
          </button>
          <button v-if="!sheetTask.done" type="button" class="dm-sheet__item ok" @click="act(() => desk.setDoneStatus(sheetTask.el.ref))">
            <v-icon>mdi-check-circle-outline</v-icon>{{ $t('setDone') }}
          </button>
          <button type="button" class="dm-sheet__item" @click="act(() => desk.copyTask('task', sheetTask.el.ref))">
            <v-icon>mdi-content-copy</v-icon>{{ $t('copy') }}
          </button>
          <button type="button" class="dm-sheet__item" @click="act(() => desk.share(sheetTask.el.ref))">
            <v-icon>mdi-share-variant-outline</v-icon>{{ $t('share') }}
          </button>
          <button v-if="sheetTask.el.ref.link" type="button" class="dm-sheet__item" @click="act(() => desk.openLink(sheetTask.el))">
            <v-icon>mdi-link-variant</v-icon>{{ $t('link') }}
          </button>
          <button type="button" class="dm-sheet__item del" @click="act(() => desk.askDelete(sheetTask.el.ref))">
            <v-icon>mdi-trash-can-outline</v-icon>{{ $t('remove') }}
          </button>
        </template>
        <template v-else>
          <button
            v-for="c in desk.columns"
            :key="c.id"
            type="button"
            class="dm-sheet__item"
            :disabled="c.id === sheetTask.col.id"
            :style="{ '--cc': c.color }"
            @click="act(() => desk.moveTo(sheetTask, c))"
          >
            <i class="dm__tab-dot" />{{ c.name }}
            <v-spacer />
            <v-icon v-if="c.id === sheetTask.col.id" small>mdi-check</v-icon>
          </button>
        </template>
      </div>
    </v-bottom-sheet>

    <!-- filters -->
    <v-dialog v-model="desk.filterOpen" fullscreen hide-overlay transition="dialog-bottom-transition">
      <div class="dm-filter">
        <div class="dm-filter__head">
          <button type="button" class="dm__icon" @click="desk.filterOpen = false">
            <v-icon>{{ $vuetify.rtl ? 'mdi-arrow-right' : 'mdi-arrow-left' }}</v-icon>
          </button>
          <span>{{ $t('filter') }}</span>
          <v-spacer />
          <button v-if="desk.activeFilterCount" type="button" class="dm-filter__clear" @click="desk.clearFilters()">
            {{ $t('taskDeskClearFilters') }}
          </button>
        </div>
        <div class="dm-filter__body">
          <desk-filters />
        </div>
        <div class="dm-filter__foot">
          <v-btn color="primary" depressed block height="44" @click="desk.applyFilters()">
            {{ $t('taskDeskApply') }}
          </v-btn>
        </div>
      </div>
    </v-dialog>
  </div>
</template>

<script>
import DeskPicker from '../../setting/components/SettingAccountPicker'
import TaskCard from './TaskCard'
import DeskFilters from './DeskFilters'
import MobileCalendar from './MobileCalendar'
import { VIEW_LIST } from './DeskToolbar'
import { claimBack } from '../backGuard'
import { today, fmt } from '../deskDates'

export default {
  name: 'DeskMobile',
  components: {
    DeskPicker,
    TaskCard,
    DeskFilters,
    MobileCalendar,
    GanttView: () => import('./GanttView'),
  },
  inject: ['desk'],
  data () {
    return {
      searchOpen: false,
      activeColId: null,
      sheet: false,
      sheetTask: null,
      moveOpen: false,
      tx: 0,
      ty: 0,
    }
  },
  computed: {
    viewList () {
      return VIEW_LIST
    },
    activeCol () {
      const cols = this.desk.columns
      return cols.find(c => c.id === this.activeColId) || cols[0] || null
    },
    colTasks () {
      return this.desk.tasks.filter(t => t.col === this.activeCol)
    },
    groups () {
      return this.desk.columns.map(col => ({ col, tasks: this.desk.tasks.filter(t => t.col === col) }))
    },
  },
  watch: {
    sheet (open) {
      if (open) {
        this.releaseSheet = claimBack('tdSheet', () => { this.sheet = false })
      } else {
        this.moveOpen = false
        if (this.releaseSheet) this.releaseSheet()
        this.releaseSheet = null
      }
    },
    'desk.filterOpen' (open) {
      if (open) {
        this.releaseFilter = claimBack('tdFilter', () => { this.desk.filterOpen = false })
      } else {
        if (this.releaseFilter) this.releaseFilter()
        this.releaseFilter = null
      }
    },
  },
  beforeDestroy () {
    if (this.releaseSheet) this.releaseSheet(false)
    if (this.releaseFilter) this.releaseFilter(false)
  },
  methods: {
    toggleSearch () {
      this.searchOpen = !this.searchOpen
      if (this.searchOpen) this.$nextTick(() => this.$refs.search && this.$refs.search.focus())
    },
    selectCol (col) {
      this.activeColId = col.id
      this.$nextTick(() => {
        const el = this.$refs['tab' + col.id]
        if (el && el[0]) el[0].scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' })
      })
    },
    openSheet (t) {
      this.sheetTask = t
      this.moveOpen = false
      this.sheet = true
    },
    // Close the sheet first (pops its history entry), then run the action.
    // Wait until the sheet's history entry has been popped before the action
    // (e.g. opening the form) pushes its own, or the late popstate would
    // close the form straight away.
    act (fn) {
      const hadEntry = !!(window.history.state && window.history.state.tdSheet)
      this.sheet = false
      if (!hadEntry) return this.$nextTick(fn)
      let done = false
      const run = () => {
        if (done) return
        done = true
        window.removeEventListener('popstate', run)
        setTimeout(fn, 0)
      }
      window.addEventListener('popstate', run)
      setTimeout(run, 400)
    },
    isLate (t) {
      return !t.done && t.end && t.end.isBefore(today())
    },
    fmtDate (m) {
      return fmt(m, 'D MMMM', this.desk.jalali)
    },
    touchStart (e) {
      this.tx = e.changedTouches[0].clientX
      this.ty = e.changedTouches[0].clientY
    },
    touchEnd (e) {
      const dx = e.changedTouches[0].clientX - this.tx
      const dy = e.changedTouches[0].clientY - this.ty
      if (Math.abs(dx) < 70 || Math.abs(dy) > 50) return
      const cols = this.desk.columns
      const i = cols.indexOf(this.activeCol)
      const forward = this.$vuetify.rtl ? dx > 0 : dx < 0
      const next = cols[i + (forward ? 1 : -1)]
      if (next) this.selectCol(next)
    },
  },
}
</script>

<style scoped>
.dm {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  position: relative;
}
.dm__top {
  flex: 0 0 auto;
  position: relative;
  background: #fff;
  padding: 8px 12px 0;
  border-bottom: 1px solid #e6e9ef;
}
.theme--dark .dm__top {
  background: #1e1e1e;
  border-color: #333;
}
.dm__row {
  display: flex;
  align-items: center;
  gap: 6px;
}
.dm__board {
  flex: 1 1 auto;
  min-width: 0;
}
.dm__icon {
  position: relative;
  flex: 0 0 auto;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #f1f3f7;
}
.theme--dark .dm__icon {
  background: #2a2a2a;
}
.dm__icon.on .v-icon {
  color: var(--v-primary-base);
}
.dm__badge {
  position: absolute;
  top: 3px;
  inset-inline-end: 3px;
  min-width: 16px;
  height: 16px;
  border-radius: 8px;
  background: var(--v-primary-base);
  color: #fff;
  font-size: 0.62rem;
  line-height: 16px;
  text-align: center;
}
.dm__search {
  margin-top: 8px;
}
.dm__views {
  display: flex;
  margin-top: 8px;
  padding: 3px;
  background: #eef1f6;
  border-radius: 8px;
}
.theme--dark .dm__views {
  background: #2a2a2a;
}
.dm__views button {
  flex: 1 1 0;
  height: 32px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  font-size: 0.75rem;
  color: #5b6475;
}
.dm__views button .v-icon {
  color: inherit;
}
.dm__views button.on {
  background: #fff;
  color: var(--v-primary-base);
  font-weight: 600;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.08);
}
.theme--dark .dm__views button.on {
  background: #1e1e1e;
}
.dm__tabs {
  display: flex;
  gap: 6px;
  overflow-x: auto;
  padding: 10px 0;
  scrollbar-width: none;
}
.dm__tabs::-webkit-scrollbar {
  display: none;
}
.dm__tab {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 12px;
  border-radius: 16px;
  border: 1px solid #e1e5ec;
  font-size: 0.8rem;
  white-space: nowrap;
}
.dm__tab.on {
  border-color: var(--cc);
  background: color-mix(in srgb, var(--cc) 12%, transparent);
  font-weight: 600;
}
.dm__tab-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--cc);
  flex: 0 0 auto;
}
.dm__tab-count {
  font-size: 0.7rem;
  padding: 0 6px;
  border-radius: 9px;
  background: rgba(0, 0, 0, 0.06);
}
.dm__loader {
  position: absolute;
  bottom: -1px;
  left: 0;
  right: 0;
}
.dm__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
}
.dm__empty {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #8a94a6;
  font-size: 0.85rem;
  padding: 24px;
}
.dm__empty--inline {
  flex: 0 0 auto;
}
.dm__board-list {
  flex: 1 0 auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 12px 96px;
}
.dm__more {
  height: 36px;
  font-size: 0.82rem;
  color: var(--v-primary-base);
  width: 100%;
}
.dm__quick {
  display: flex;
  align-items: center;
  background: #fff;
  border: 1px dashed #c5ccd6;
  border-radius: 8px;
  padding: 0 4px 0 12px;
  padding-inline-start: 12px;
}
.theme--dark .dm__quick {
  background: #1e1e1e;
}
.dm__quick input {
  flex: 1 1 auto;
  height: 44px;
  outline: none;
  font-size: 0.88rem;
  min-width: 0;
  color: inherit;
}
.dm__quick-send {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: var(--v-primary-base);
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.dm__quick-send .v-icon {
  color: #fff;
}
.v-application--is-rtl .dm__quick-send .v-icon {
  transform: scaleX(-1);
}
.dm__list {
  padding-bottom: 96px;
}
.dm__group {
  position: sticky;
  top: 0;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  font-weight: 700;
  font-size: 0.85rem;
  background: #f3f5f9;
}
.theme--dark .dm__group {
  background: #121212;
}
.dm__lrow {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 6px 10px 14px;
  padding-inline-start: 14px;
  background: #fff;
  border-bottom: 1px solid #eef0f4;
  border-inline-start: 3px solid var(--cc);
}
.theme--dark .dm__lrow {
  background: #1e1e1e;
  border-bottom-color: #2a2a2a;
}
.dm__lrow-main {
  flex: 1 1 auto;
  min-width: 0;
}
.dm__lrow-title {
  font-size: 0.88rem;
  font-weight: 600;
  word-break: break-word;
}
.dm__lrow.done .dm__lrow-title {
  color: #8a94a6;
  text-decoration: line-through;
}
.dm__lrow-sub {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 2px;
  font-size: 0.74rem;
  color: #6b7280;
}
.dm__lrow-sub .late {
  color: #c2185b;
  font-weight: 600;
}
.dm__lrow-sub .v-icon {
  color: inherit;
}
.dm__lrow-more {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  flex: 0 0 auto;
}
.dm__gantt {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding: 8px 8px calc(80px + env(safe-area-inset-bottom));
}
.dm__fab {
  position: absolute;
  bottom: calc(18px + env(safe-area-inset-bottom));
  inset-inline-end: 18px;
  z-index: 3;
  width: 56px;
  height: 56px;
  border-radius: 16px;
  background: var(--v-primary-base);
  box-shadow: 0 6px 16px rgba(16, 24, 40, 0.25);
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.dm-sheet {
  background: #fff;
  border-radius: 14px 14px 0 0;
  padding: 8px 8px calc(12px + env(safe-area-inset-bottom));
  max-height: 80vh;
  overflow-y: auto;
}
.theme--dark .dm-sheet {
  background: #1e1e1e;
}
.dm-sheet__grip {
  width: 40px;
  height: 4px;
  border-radius: 2px;
  background: #d5dae2;
  margin: 2px auto 10px;
}
.dm-sheet__title {
  font-weight: 700;
  padding: 0 12px;
  word-break: break-word;
}
.dm-sheet__status {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px 8px;
  font-size: 0.78rem;
  color: #6b7280;
  border-bottom: 1px solid #eef0f4;
  margin-bottom: 4px;
}
.dm-sheet__item {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 14px;
  height: 50px;
  padding: 0 12px;
  border-radius: 8px;
  font-size: 0.92rem;
  text-align: start;
}
.dm-sheet__item:active {
  background: #f1f3f7;
}
.dm-sheet__item:disabled {
  opacity: 0.5;
}
.dm-sheet__item .v-icon {
  color: #6b7280;
}
.dm-sheet__item.ok .v-icon {
  color: #2e7d32;
}
.dm-sheet__item.del {
  color: #d81b60;
}
.dm-sheet__item.del .v-icon {
  color: inherit;
}
.dm-filter {
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #fff;
}
.theme--dark .dm-filter {
  background: #1e1e1e;
}
.dm-filter__head {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  font-weight: 700;
  border-bottom: 1px solid #eef0f4;
}
.dm-filter__clear {
  font-size: 0.82rem;
  font-weight: 400;
  color: var(--v-primary-base);
}
.dm-filter__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 16px;
}
.dm-filter__body ::v-deep .v-input {
  height: auto !important;
}
.dm-filter__foot {
  flex: 0 0 auto;
  padding: 10px 16px calc(10px + env(safe-area-inset-bottom));
  border-top: 1px solid #eef0f4;
}
</style>
